import { useAsyncData, useRoute } from '#imports'
import { computed } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

export function useFetchPickersInternal(route = useRoute()) {
  const { grade, offerType, variantClicked, productId } = useUrlParams(route)
  const $httpFetch = useHttpFetch()

  const pickersQueryParams = computed(() => {
    const values: ApiSpecs['/product-page/products/:productId/pickers']['GET']['query'] =
      {
        grade: grade.value ? String(grade.value) : null,
        special_offer_type: offerType.value ? String(offerType.value) : null,
        premium_grade: true,
        cheapest_by_grade: !variantClicked.value,
      }

    return removeEmptyValuesInObject(values)
  })

  return useAsyncData(
    () =>
      getAsyncDataKey('pp-pickers', {
        productId,
        ...pickersQueryParams.value,
      }),
    async () => {
      let pickersResponseCacheTagHeader

      const response = await $httpFetch(
        '/product-page/products/:productId/pickers',
        {
          pathParams: { productId },
          query: pickersQueryParams.value,
          onResponse: (context) => {
            if (context.response?.ok) {
              pickersResponseCacheTagHeader =
                context.response?.headers.get('Cache-Tag') ?? ''
            }
          },
        },
      )

      return {
        ...response,
        pickersResponseCacheTagHeader,
      }
    },
    {
      dedupe: 'defer',
      getCachedData: (key, nuxtApp) => {
        // Prevent duplicate calls during SSR: after ProductDetailPageBase's
        // `onServerPrefetch` resolves, child components find the data here
        // instead of triggering a second HTTP call.
        if (import.meta.server || nuxtApp.isHydrating) {
          return nuxtApp.payload.data[key]
        }

        // After hydration: always fetch fresh data.
        // This endpoint is the source of truth for price — never serve stale.
        return undefined
      },
    },
  )
}
